/**
 * TO-BE(Nuxt) → AS-IS(JSP) 강제 전체 페이지 이동 유틸리티
 *
 * 목적:
 * - Nuxt SPA 라우팅을 거치지 않고, 브라우저 레벨에서 Full Page Reload로 AS-IS로 이동
 * - useRouter / navigateTo 등 Nuxt 내장 라우팅을 사용하지 않음
 * - 동일 8080 서버 내 이동으로 세션 유지
 *
 * 사용 예:
 *   const { navigateToAsis } = useNavigateToAsis();
 *   navigateToAsis({ entryPath: '/hcm/main.do', menuId: 'MENU_001' });
 */

/** 개발: Spring 서버(8080) 직접 주소, 운영: 같은 도메인(빈 문자열 → 상대 경로) */
function getAsisOrigin(): string {
  if (import.meta.server) return '';

  // 브라우저 환경에서 현재 도메인을 가져옴
  const currentHost = window.location.host;

  // 1. 로컬 환경 (localhost)
  if (currentHost.includes('localhost')) {
    return 'http://localhost:8085';
  }

  // 2. 개발 서버 환경 (예: dev.strategygate.biz)
  if (currentHost.includes('dev.')) {
    // 개발서버 - 현재 도메인을 기준으로 이동하면됨
    return '';
  }

  // 3. 운영 환경 (www.sgate.biz 등) - 상대 경로
  return '';
}

export interface NavigateToAsisOptions {
  /**
   * AS-IS 진입점 경로 (Context Path 포함).
   * - 예: '/main.do', '/hcm/main.do'
   * - 미지정 시 '/main.do' 사용.
   */
  entryPath?: string;
  /** URL 해시(Fragment). 넘겨준 값 그대로 # 뒤에 붙임 (예: /main.do#PGM1722) */
  hash?: string;
  /**
   * true일 때만 쿼리 파라미터(menuId, pgmId, extra, fromNuxt)를 URL에 포함.
   * 기본값 false: 쿼리 없이 entryPath + hash만 사용 (AS-IS 보안 이슈 방지).
   */
  useQueryParams?: boolean;
  /** useQueryParams가 true일 때만 사용. AS-IS 메뉴 활성화용 메뉴 ID */
  menuId?: string;
  /** useQueryParams가 true일 때만 사용. 프로그램 ID */
  pgmId?: string;
  /** useQueryParams가 true일 때만 사용. 추가 쿼리 파라미터 */
  extra?: Record<string, string>;
}

const DEFAULT_ENTRY_PATH = '/main.do';

/**
 * AS-IS(JSP) 페이지로 브라우저 강제 이동(Full Page Reload)을 수행합니다.
 * - useQueryParams 기본 false: 쿼리 없이 entryPath + hash만 사용 (/main.do#PGM1722 형태).
 * - Nuxt 라우터를 사용하지 않고 window.location.assign()만 사용합니다.
 *
 * @param options entryPath, hash, useQueryParams, menuId, pgmId, extra
 */
export function useNavigateToAsis() {
  const navigateToAsis = (options: NavigateToAsisOptions = {}) => {
    const origin = getAsisOrigin();
    const entryPath = options.entryPath ?? DEFAULT_ENTRY_PATH;
    const path = entryPath.startsWith('/') ? entryPath : `/${entryPath}`;

    const hashPart = options.hash != null && options.hash !== '' ? `#${options.hash}` : '';

    let queryPart = '';
    if (options.useQueryParams) {
      const params = new URLSearchParams();
      if (options.menuId) params.set('menuId', options.menuId);
      if (options.pgmId) params.set('pgmId', options.pgmId);
      if (options.extra) {
        Object.entries(options.extra).forEach(([k, v]) => params.set(k, v));
      }
      params.set('fromNuxt', '1');
      const query = params.toString();
      queryPart = query ? `?${query}` : '';
    }

    const fullUrl = `${origin}${path}${queryPart}${hashPart}`;

    if (import.meta.dev && typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.log('[useNavigateToAsis] Full page redirect (AS-IS)', {
        entryPath: path,
        origin: origin || '(same origin)',
        hash: options.hash || '(none)',
        fullUrl,
      });
    }

    if (typeof window !== 'undefined') {
      window.location.assign(fullUrl);
    }
  };

  return { navigateToAsis };
}
